(() => {
  console.log("BUILD", Date.now());
  /* -------------------------------------------------------
     0) One-shot guard to avoid double init
     ------------------------------------------------------- */
  if (window.__siteMainInited) return;
  window.__siteMainInited = true;

  /* -------------------------------------------------------
     1) Helpers
     ------------------------------------------------------- */
  const qs = (sel, root = document) => root.querySelector(sel);
  const qsa = (sel, root = document) => Array.from(root.querySelectorAll(sel));
  const on = (el, ev, fn, opts) => el && el.addEventListener(ev, fn, opts);

  async function inject(selector, url) {
    const host = qs(selector);
    if (!host) return false;
    try {
      const res = await fetch(url, { cache: "no-cache" });
      host.innerHTML = await res.text();
      return true;
    } catch (_) {
      return false;
    }
  }

  /* -------------------------------------------------------
     2) Mobile drawer menu
     ------------------------------------------------------- */
  function initMobileMenu() {
    if (document.documentElement.dataset.mobileMenuInited === "1") return;

    const drawer = document.getElementById("mobileMenu") || qs(".mobile-menu");
    const toggles = qsa(".mobile-menu-toggle");
    if (!drawer || !toggles.length) {
      document.documentElement.dataset.mobileMenuInited = "1";
      return;
    }

    const panels = qsa(".mobile-panel", drawer);
    const backdrop = qs(".mobile-backdrop", drawer);
    const lockScroll = (on) =>
      (document.body.style.overflow = on ? "hidden" : "");

    const showPanel = (name) => {
      panels.forEach((p) => {
        const active = p.dataset.panel === name;
        p.classList.toggle("active", active);
        p.setAttribute("aria-hidden", active ? "false" : "true");
      });
    };
    const openDrawer = () => {
      drawer.classList.add("open");
      lockScroll(true);
      showPanel("root");
    };
    const closeDrawer = () => {
      drawer.classList.remove("open");
      lockScroll(false);
      showPanel("root");
    };

    toggles.forEach((btn) =>
      on(btn, "click", (e) => {
        e.preventDefault();
        drawer.classList.contains("open") ? closeDrawer() : openDrawer();
      }),
    );

    on(drawer, "click", (e) => {
      const openBtn = e.target.closest("[data-open]");
      const backBtn = e.target.closest("[data-back]");
      const closeBtn = e.target.closest(".mobile-close");
      const backdropHit = e.target.closest(".mobile-backdrop");

      if (openBtn) {
        e.preventDefault();
        showPanel(openBtn.getAttribute("data-open"));
        return;
      }
      if (backBtn) {
        e.preventDefault();
        showPanel("root");
        return;
      }
      if (closeBtn || backdropHit) {
        e.preventDefault();
        closeDrawer();
        return;
      }

      const link = e.target.closest('a.mobile-link[href]:not([href="#"])');
      if (link) closeDrawer();
    });

    on(document, "keydown", (e) => {
      if (e.key === "Escape" && drawer.classList.contains("open"))
        closeDrawer();
    });

    showPanel("root");
    document.documentElement.dataset.mobileMenuInited = "1";
  }

  /* -------------------------------------------------------
     3) Back-to-top
     ------------------------------------------------------- */
  function initBackToTop() {
    const backToTop = qs(".back-to-top") || qs(".to-top");
    if (!backToTop || backToTop.dataset.btInited === "1") return;

    let sentinel = document.getElementById("top");
    if (!sentinel) {
      sentinel = document.createElement("div");
      sentinel.id = "top";
      sentinel.style.position = "absolute";
      sentinel.style.top = "0";
      document.body.prepend(sentinel);
    }
    if ("IntersectionObserver" in window) {
      const io = new IntersectionObserver(
        (entries) =>
          backToTop.classList.toggle("show", !entries[0].isIntersecting),
        { root: null, threshold: 0 },
      );
      io.observe(sentinel);
    } else {
      const toggleBtn = () =>
        backToTop.classList.toggle(
          "show",
          (window.scrollY || document.documentElement.scrollTop) > 200,
        );
      toggleBtn();
      on(window, "scroll", toggleBtn, { passive: true });
    }
    on(backToTop, "click", (e) => {
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: "smooth" });
    });

    backToTop.dataset.btInited = "1";
  }

  /* -------------------------------------------------------
     5) Services Accordion
     ------------------------------------------------------- */
  function initServicesAccordion() {
    if (document.documentElement.dataset.svcAccInited === "1") return;

    const root = document.querySelector("#services");
    if (!root) {
      document.documentElement.dataset.svcAccInited = "1";
      return;
    }

    const items = Array.from(root.querySelectorAll(".service"))
      .map((wrap) => {
        const btn = wrap.querySelector(".service-head");
        if (!btn) return null;

        const ctrlId = btn.getAttribute("aria-controls");
        let panel = ctrlId ? document.getElementById(ctrlId) : null;
        if (!panel) {
          const next = btn.nextElementSibling;
          if (next && next.classList.contains("service-body")) panel = next;
        }
        if (!panel) return null;

        const isOpen =
          !panel.hasAttribute("hidden") && panel.classList.contains("show");
        btn.setAttribute("aria-expanded", isOpen ? "true" : "false");
        wrap.classList.toggle("open", isOpen);
        if (isOpen) {
          panel.classList.add("show");
          panel.removeAttribute("hidden");
        } else {
          panel.classList.remove("show");
          panel.setAttribute("hidden", "");
        }

        if (!btn.hasAttribute("type")) btn.setAttribute("type", "button");
        return { wrap, btn, panel };
      })
      .filter(Boolean);

    const closeItem = ({ wrap, btn, panel }) => {
      btn.setAttribute("aria-expanded", "false");
      wrap.classList.remove("open");
      panel.classList.remove("show");
      panel.setAttribute("hidden", "");
    };
    const openItem = ({ wrap, btn, panel }) => {
      btn.setAttribute("aria-expanded", "true");
      wrap.classList.add("open");
      panel.classList.add("show");
      panel.removeAttribute("hidden");
    };
    const toggleItem = (targetBtn) => {
      const item = items.find(({ btn }) => btn === targetBtn);
      if (!item) return;
      const isOpen = item.btn.getAttribute("aria-expanded") === "true";
      if (isOpen) closeItem(item);
      else openItem(item);
    };

    root.addEventListener("click", (e) => {
      const btn = e.target.closest(".service-head");
      if (!btn || !root.contains(btn)) return;
      e.preventDefault();
      toggleItem(btn);
    });

    root.addEventListener("keydown", (e) => {
      const btn = e.target.closest(".service-head");
      if (!btn || !root.contains(btn)) return;
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        toggleItem(btn);
      }
    });

    document.documentElement.dataset.svcAccInited = "1";
  }

  /* -------------------------------------------------------
   Lightbox
------------------------------------------------------- */
  function initLightbox() {
    if (document.documentElement.dataset.lightboxInited === "1") return;
    if (!document.body.classList.contains("video-page")) {
      document.documentElement.dataset.lightboxInited = "1";
      return;
    }

    const lb = qs("#lightbox");
    const imgEl = qs("#lightbox-img");
    const ytEl = qs("#lightbox-video");
    const mp4El = qs("#lightbox-mp4");
    const capEl = qs("#lightbox-caption");
    const btnPrev = lb?.querySelector(".prev");
    const btnNext = lb?.querySelector(".next");
    const btnClose = lb?.querySelector(".close");

    if (!lb || !imgEl || !capEl || !btnClose) {
      document.documentElement.dataset.lightboxInited = "1";
      return;
    }

    // caption 整形
    function formatCaption(caption) {
      if (!caption) return "";
      const normalized = String(caption)
        .replace(/&lt;br\s*\/?&gt;/gi, "<br>")
        .replace(/\n/g, "<br>");
      const parts = normalized.split(/<br\s*\/?>/i);
      return parts.length > 1
        ? `<strong>${parts[0]}</strong><br>${parts.slice(1).join("<br>")}`
        : `<strong>${normalized}</strong>`;
    }

    // 初期状態は非表示
    lb.setAttribute("hidden", "");
    document.body.classList.remove("lightbox-open");

    let gallery = [];
    let index = 0;

    const isYouTube = (url = "") =>
      /^(https?:)?\/\/(www\.)?youtube\.com\/|^(https?:)?\/\/youtu\.be\//i.test(
        url,
      );
    const isMp4 = (url = "") => /\.mp4(\?|#|$)/i.test(url);

    const removeYTFallback = () => {
      lb.querySelectorAll(".yt-fallback").forEach((n) => n.remove());
    };

    // すべて停止
    const stopAll = () => {
      removeYTFallback();
      if (ytEl) {
        ytEl.removeAttribute("src");
        ytEl.style.display = "none";
        ytEl.setAttribute("hidden", "");
      }
      if (mp4El) {
        mp4El.pause?.();
        mp4El.removeAttribute("src");
        mp4El.style.display = "none";
        mp4El.setAttribute("hidden", "");
        // load() は呼ばない（iOS で自動再生が切られやすい）
      }
      imgEl.style.display = "none";
      imgEl.setAttribute("hidden", "");
      lb.classList.remove("show-yt");
    };

    const open = (isReel = false) => {
      lb.classList.toggle("is-reel", isReel);
      lb.removeAttribute("hidden");
      lb.classList.add("is-active");
      document.body.classList.add("lightbox-open");
    };

    const close = () => {
      stopAll();
      lb.classList.remove("is-active", "is-reel");
      lb.setAttribute("hidden", "");
      document.body.classList.remove("lightbox-open");
      gallery = [];
      index = 0;
      imgEl.removeAttribute("src");
      capEl.textContent = "";
    };

    // レンダリング
    const render = () => {
      if (!gallery.length) return close();
      stopAll();

      const src = gallery[index];
      const isVideo = isYouTube(src) || isMp4(src);

      if (isYouTube(src) && ytEl) {
        const url = src.includes("?")
          ? `${src}&autoplay=1&rel=0&modestbranding=1&playsinline=1`
          : `${src}?autoplay=1&rel=0&modestbranding=1&playsinline=1`;

        ytEl.src = url;
        ytEl.style.display = "block";
        ytEl.removeAttribute("hidden");
        lb.classList.add("show-yt");

        // 万一制限で埋め込めない時のフォールバック
        const watchUrl = src.replace(/\/embed\//, "/watch?v=");
        const timer = setTimeout(() => {
          const a = document.createElement("a");
          a.textContent = "Open on YouTube";
          a.href = watchUrl;
          a.target = "_blank";
          a.rel = "noopener";
          a.className = "yt-fallback";
          capEl.insertAdjacentElement("afterend", a);
        }, 1500);
        ytEl.addEventListener("load", () => clearTimeout(timer), {
          once: true,
        });
      } else if (isMp4(src) && mp4El) {
        // ---- iOS Safari でも確実に自動再生させる順序 ----
        mp4El.hidden = false;
        mp4El.style.display = "block";

        // 先に属性を付与（attribute も）
        mp4El.muted = true;
        mp4El.setAttribute("muted", "");
        mp4El.autoplay = true;
        mp4El.setAttribute("autoplay", "");
        mp4El.setAttribute("playsinline", "");
        mp4El.setAttribute("webkit-playsinline", "");
        // 再生開始までは controls を隠す（干渉回避）
        mp4El.removeAttribute("controls");

        // src セット（load() は呼ばない）
        mp4El.pause();
        mp4El.removeAttribute("src");
        mp4El.src = src;

        const tryPlay = () => {
          const p = mp4El.play?.();
          if (p && typeof p.catch === "function") p.catch(() => {});
        };
        // 即時 & イベントで再試行
        tryPlay();
        mp4El.addEventListener("loadedmetadata", tryPlay, { once: true });
        mp4El.addEventListener("canplay", tryPlay, { once: true });
        mp4El.addEventListener("stalled", tryPlay);

        // 再生が始まったら controls を出す
        mp4El.addEventListener(
          "playing",
          () => {
            mp4El.controls = true;
          },
          { once: true },
        );

        lb.classList.remove("show-yt");
      } else {
        // 画像
        imgEl.src = src;
        imgEl.style.display = "block";
        imgEl.removeAttribute("hidden");
        lb.classList.remove("show-yt");
      }

      // caption は常に下に
      const wrapper = lb.querySelector(".lightbox-content-wrapper") || lb;
      if (capEl && wrapper) wrapper.appendChild(capEl);

      if (isVideo) capEl.classList.add("light");
      else capEl.classList.remove("light");

      const showNav = gallery.length > 1;
      if (btnPrev) btnPrev.style.visibility = showNav ? "visible" : "hidden";
      if (btnNext) btnNext.style.visibility = showNav ? "visible" : "hidden";
    };

    // ナビゲーションボタンを wrapper 内に移動
    const wrapper = lb.querySelector(".lightbox-content-wrapper");
    if (wrapper) {
      ["prev", "next"].forEach((cls) => {
        const el = lb.querySelector("." + cls);
        if (el && el.parentElement !== wrapper) wrapper.appendChild(el);
      });
    }

    // クリックで開く
    qsa(".portfolio-item, .reel-item").forEach((item) => {
      on(item, "click", () => {
        // data-watch がある場合は外部リンクを即時オープン
        const watchUrl = item.getAttribute("data-watch");
        if (watchUrl) {
          window.open(watchUrl, "_blank", "noopener");
          return;
        }

        const videoUrl = item.getAttribute("data-video") || "";
        const imageUrl =
          item.getAttribute("data-image") ||
          item.querySelector("img")?.getAttribute("src") ||
          "";
        const caption = item.getAttribute("data-caption") || "";
        capEl.innerHTML = formatCaption(caption);

        const isReel = item.classList.contains("reel-item");
        open(isReel);

        if (videoUrl) {
          gallery = [videoUrl];
          index = 0;
          render();
          return;
        }

        const set = [];
        if (imageUrl) set.push(imageUrl);
        const extra = (item.getAttribute("data-gallery") || "")
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean);
        extra.forEach((s) => {
          if (!set.includes(s)) set.push(s);
        });

        gallery = set;
        index = 0;
        render();
      });
    });

    // イベント
    on(btnPrev, "click", () => {
      if (!gallery.length) return;
      index = (index - 1 + gallery.length) % gallery.length;
      render();
    });
    on(btnNext, "click", () => {
      if (!gallery.length) return;
      index = (index + 1) % gallery.length;
      render();
    });
    on(btnClose, "click", close);
    on(lb, "click", (e) => {
      if (e.target === lb) close();
    });
    on(document, "keydown", (e) => {
      if (!lb.classList.contains("is-active")) return;
      if (e.key === "Escape") return close();
      if (!gallery.length) return;
      if (e.key === "ArrowLeft") btnPrev?.click();
      if (e.key === "ArrowRight") btnNext?.click();
    });

    document.documentElement.dataset.lightboxInited = "1";
  }

  /* -------------------------------------------------------
     6.5) 新ギャラリーモーダル（画像スクロール）
     ------------------------------------------------------- */
  function initGalleryModal() {
    if (document.documentElement.dataset.gmInited === "1") return;

    // 追加：Videoページではギャラリーモーダルを使わない
    if (document.body.classList.contains("video-page")) {
      document.documentElement.dataset.gmInited = "1";
      return;
    }

    const modal = document.getElementById("galleryModal");
    const body = document.getElementById("gmBody");
    const closeBtn = modal?.querySelector(".gm-close");
    const items = document.querySelectorAll(".portfolio-item");
    if (!modal || !body || !items.length) {
      document.documentElement.dataset.gmInited = "1";
      return;
    }

    const stopScroll = (on) => {
      document.body.style.overflow = on ? "hidden" : "";
    };

    const open = () => {
      modal.removeAttribute("hidden");
      modal.classList.add("is-active");
      stopScroll(true);
      body.focus({ preventScroll: true });
    };

    const close = () => {
      modal.classList.remove("is-active");
      modal.setAttribute("hidden", "");
      stopScroll(false);
      body.innerHTML = "";
    };

    const makeFigure = (src, captionHTML = "") => {
      const fig = document.createElement("figure");
      fig.className = "gm-figure";

      const img = document.createElement("img");
      img.loading = "lazy";
      img.decoding = "async";
      img.src = src;

      const figcap = document.createElement("figcaption");
      figcap.className = "gm-cap";

      // ★ 前半を太字に変換
      if (captionHTML.includes("<br>")) {
        const [first, ...rest] = captionHTML.split("<br>");
        figcap.innerHTML = `<strong>${first}</strong><br>${rest.join("<br>")}`;
      } else {
        figcap.innerHTML = `<strong>${captionHTML}</strong>`;
      }

      fig.append(img, figcap);
      return fig;
    };

    items.forEach((it) => {
      it.addEventListener("click", (e) => {
        e.preventDefault();

        const main =
          it.getAttribute("data-image") ||
          it.querySelector("img")?.getAttribute("src") ||
          "";
        const extras = (it.getAttribute("data-gallery") || "")
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean);
        const captionHTML = it.getAttribute("data-caption") || "";

        const gallery = [];
        if (main) gallery.push(main);
        extras.forEach((g) => {
          if (!gallery.includes(g)) gallery.push(g);
        });

        body.innerHTML = "";
        gallery.forEach((src, i) => {
          const cap = i === 0 ? captionHTML : ""; // ★ 1枚目だけキャプション
          body.appendChild(makeFigure(src, cap));
        });

        open();
        body.scrollTo({ top: 0, left: 0, behavior: "instant" });
      });
    });

    closeBtn?.addEventListener("click", close);
    modal.addEventListener("click", (ev) => {
      if (ev.target === modal) close();
    });
    document.addEventListener("keydown", (ev) => {
      if (!modal.classList.contains("is-active")) return;
      if (ev.key === "Escape") close();
    });

    document.documentElement.dataset.gmInited = "1";
  }

  /* -------------------------------------------------------
     7) Active marker 
     ------------------------------------------------------- */
  function markActiveNav() {
    const nav = qs("#site-header nav, nav.navbar");
    if (!nav) return;

    let file = (location.pathname.split("/").pop() || "").toLowerCase();
    if (!file || file === "/") file = "index.html";

    let matched = false;
    qsa("a[href]", nav).forEach((a) => {
      const href = (a.getAttribute("href") || "")
        .split("/")
        .pop()
        .toLowerCase();
      if (href && href === file) {
        a.classList.add("is-active");
        a.setAttribute("aria-current", "page");
        a.closest(".dropdown")
          ?.querySelector(".dropdown-toggle")
          ?.classList.add("is-active");
        matched = true;
      }
    });

    if (!matched) {
      const b = document.body;
      const section = b.classList.contains("video-page")
        ? "video"
        : b.classList.contains("graphic-page")
          ? "graphic"
          : b.classList.contains("website-page")
            ? "website"
            : b.classList.contains("apparel-page")
              ? "apparel"
              : b.classList.contains("illustration-page")
                ? "illustration"
                : b.classList.contains("contact-page")
                  ? "contact"
                  : b.classList.contains("about-page")
                    ? "about"
                    : "home";

      qsa(`[data-section="${section}"]`, nav).forEach((a) => {
        a.classList.add("is-active");
        a.setAttribute("aria-current", "page");
        if (a.closest(".dropdown"))
          qs("#worksDropdown")?.classList.add("is-active");
      });
    }

    qsa("#mobileMenu a.mobile-link[data-section]").forEach((a) => {
      const sec = a.getAttribute("data-section");
      const activeInNav = nav.querySelector(
        `.is-active[data-section="${sec}"]`,
      );
      if (activeInNav) {
        a.classList.add("is-active");
        a.setAttribute("aria-current", "page");
      }
    });

    qsa("#mobileMenu a.mobile-link[href]").forEach((a) => {
      const hrefFile = (a.getAttribute("href") || "")
        .split("/")
        .pop()
        .toLowerCase();
      if (hrefFile && hrefFile === file) {
        a.classList.add("is-active");
        a.setAttribute("aria-current", "page");
      }
    });

    const worksRoot = qs(
      '#mobileMenu .mobile-panel[data-panel="root"] .mobile-link.has-child[data-open="works"]',
    );
    const hasActiveChild =
      qsa(
        '#mobileMenu .mobile-panel[data-panel="works"] .mobile-list .mobile-link.is-active',
      ).length > 0;
    if (worksRoot && hasActiveChild) {
      worksRoot.classList.add("is-active");
      worksRoot.setAttribute("aria-current", "true");
    }

    const isAbout =
      file === "about.html" || document.body.classList.contains("about-page");
    if (isAbout) {
      const desktopAbout =
        nav.querySelector('.nav-link[href="about.html"]') ||
        nav.querySelector('.nav-link[data-section="about"]');
      if (desktopAbout) {
        desktopAbout.classList.add("is-active");
        desktopAbout.setAttribute("aria-current", "page");
      }

      const mobileAbout =
        document.querySelector('#mobileMenu .mobile-link[href="about.html"]') ||
        document.querySelector(
          '#mobileMenu .mobile-link[data-section="about"]',
        ) ||
        document.querySelector(
          '#mobileMenu .mobile-link[data-i18n="nav.about"]',
        );
      if (mobileAbout) {
        mobileAbout.classList.add("is-active");
        mobileAbout.setAttribute("aria-current", "page");
      }
    }
  }

  /* -------------------------------------------------------
     8) i18n dictionary
     ------------------------------------------------------- */
  const I18N = {
    en: {
      "intro.line1": "Creating a Better World Through the Power of Design.",
      "intro.line2":
        "Graphic design, branding, ad creative, SNS marketing, video editing, and web development.",
      "intro.line3":
        "I shape the value of brands and services into creative work that drives results.",
      "services.brand.1": "Brand Strategy / Naming / Tone of Voice",
      "services.brand.2": "Logo & Identity System / Brand Guidelines",
      "services.brand.3": "Campaign Creative / Social Media Assets",
      "services.brand.4": "Marketing Collateral (Posters / Flyers / OOH)",
      "services.print.1": "Menus / Brochures / Catalogues",
      "services.print.2": "Business Cards / Stationery / Packaging",
      "services.print.3":
        "Photography & Retouching (Products / Food / Advertising)",
      "services.print.4": "Apparel Design (T-shirts / Tags / Merchandise)",
      "services.print.5": "Presentation Materials & Document Design",
      "services.print.6": "Signage Design (Indoor / Outdoor / Shopfronts)",
      "services.web.1": "Responsive Websites (HTML / CSS / JavaScript / PHP)",
      "services.web.2": "WordPress / Headless CMS / Performance Tuning",
      "services.web.3": "UI/UX Design / Landing Pages",
      "services.web.4": "Video Editing",
      "services.web.5": "Motion Graphics",

      // About
      "about.name": "Moe Miyazawa (Graphic & Web Designer)",
      "about.desc.1":
        "Since 2019, I've worked across Korea and Australia — with graphic design at the core, spanning branding, ad creative production, SNS marketing, video editing, and web development.",
      "about.desc.2":
        "With a marketing perspective, I bring brand and service stories to life, pursuing creative work that drives real results.",
      "about.desc.3": "Experienced working in Japanese, Korean, and English.",
      "about.strengths.title": "CREATIVE STRENGTHS",
      "about.strengths.1": "Branding & Visual Identity",
      "about.strengths.2": "Minimal & Refined Design",
      "about.strengths.3": "Typography & Layout Expertise",
      "about.strengths.4": "Storytelling through Visuals",
      "about.strengths.5": "Cross-cultural Collaboration",
      "about.strengths.6": "Consistency & Attention to Detail",
      "about.edu.title": "EDUCATION",
      "about.edu.1": "Bachelor of Tourism — Tokai University (Tokyo)",
      "about.edu.2":
        "Certificate IV in Marketing & Communication — Greystone College (Sydney)",
      "about.edu.3":
        "Diploma of Graphic Design — Australian Pacific College (Sydney)",
      "about.edu.4":
        "Certificate IV & Diploma of Web Development — TAFE NSW (Sydney)",
      "about.langs.jp": "Japanese — Native",
      "about.langs.kr": "Korean — Proficient (TOPIK 6)",
      "about.langs.en": "English — Advanced",

      // Contact
      "contact.firstName": "First Name",
      "contact.lastName": "Last Name",
      "contact.service": "Service",
      "contact.service.select": "Select",
      "contact.service.graphic": "Graphic Design",
      "contact.service.brand": "Branding",
      "contact.service.web": "Website",
      "contact.service.video": "Video",
      "contact.service.apparel": "Apparel / Merchandise",
      "contact.service.other": "Other",
      "contact.email": "Email",
      "contact.message": "Message",
      "contact.send": "Send",
      "contact.err.firstName": "Please enter your first name.",
      "contact.err.email": "Please enter a valid email address.",
      "contact.err.message": "Please enter at least 10 characters.",
      "contact.success": "Thanks! Your message has been sent.",
      "contact.error":
        "Sorry—couldn’t send. Please try again or email me directly.",
      "contact.sending": "Sending…",
      "contact.required": "Required",
    },

    jp: {
      "intro.line1": "Creating a Better World Through the Power of Design.",
      "intro.line2":
        "グラフィックデザイン、ブランディング、広告クリエイティブ制作、SNSマーケティング、動画編集、Web制作。",
      "intro.line3":
        "ブランドやサービスの価値を形にし、成果につながるクリエイティブを制作します。",
      "services.brand.1": "ブランド戦略 / ネーミング / ボイス＆トーン設計",
      "services.brand.2":
        "ロゴ・アイデンティティシステム / ブランドガイドライン",
      "services.brand.3": "キャンペーンクリエイティブ / SNSコンテンツ制作",
      "services.brand.4":
        "マーケティングツール（ポスター / フライヤー / 屋外広告）",
      "services.print.1": "メニュー / パンフレット / カタログ",
      "services.print.2": "名刺 / グッズ / パッケージ",
      "services.print.3": "商品・フード・広告写真の撮影＆レタッチ",
      "services.print.4": "アパレルデザイン（Tシャツ / タグ）",
      "services.print.5": "プレゼン資料 / 各種資料デザイン",
      "services.print.6": "看板デザイン（屋内 / 屋外 / 店舗）",
      "services.web.1": "レスポンシブサイト（HTML / CSS / JavaScript / PHP）",
      "services.web.2": "WordPress / ヘッドレスCMS / パフォーマンス最適化",
      "services.web.3": "UI・UXデザイン / LP",
      "services.web.4": "動画編集",
      "services.web.5": "モーションデザイン / モーショングラフィックス",

      "about.name": "宮沢萌（グラフィック・ウェブデザイナー）",
      "about.desc.1":
        "2019年より、グラフィックデザインを軸に、韓国およびオーストラリアにてブランディング、広告クリエイティブ制作、SNSマーケティング、動画編集、Web制作まで幅広く活動。",
      "about.desc.2":
        "マーケティングの視点を持ちながら、ブランドやサービスの想いやストーリーを形にし、成果につながるクリエイティブを追求。",
      "about.desc.3": "日本語・韓国語・英語での実務経験あり。",
      "about.strengths.1": "ブランディング / ビジュアルアイデンティティ",
      "about.strengths.2": "ミニマルで洗練されたデザイン",
      "about.strengths.3": "タイポグラフィ / レイアウト設計",
      "about.strengths.4": "ストーリー性のあるビジュアル",
      "about.strengths.5": "グローバルなプロジェクト連携",
      "about.strengths.6": "ディテールを極めた仕上げ",
      "about.edu.1": "学士（観光学）- 東海大学（東京）",
      "about.edu.2":
        "マーケティング&コミュニケーション（Certificate IV）- Greystone College（シドニー）",
      "about.edu.3":
        "グラフィックデザイン（Diploma）- Australian Pacific College（シドニー）",
      "about.edu.4":
        "Web開発（Certificate IV / Diploma）- TAFE NSW（シドニー）",
      "about.langs.jp": "日本語 - ネイティブ",
      "about.langs.kr": "韓国語 - 上級（TOPIK 6級）",
      "about.langs.en": "英語 - 上級",

      "contact.firstName": "名",
      "contact.lastName": "姓",
      "contact.service": "ご依頼内容",
      "contact.service.select": "選択してください",
      "contact.service.graphic": "グラフィックデザイン",
      "contact.service.brand": "ブランディング",
      "contact.service.web": "ウェブサイト",
      "contact.service.video": "動画",
      "contact.service.apparel": "アパレル / グッズ",
      "contact.service.other": "その他",
      "contact.email": "メールアドレス",
      "contact.message": "メッセージ",
      "contact.send": "送信",
      "contact.err.firstName": "名を入力してください。",
      "contact.err.email": "有効なメールアドレスを入力してください。",
      "contact.err.message": "10文字以上で入力してください。",
      "contact.success": "ありがとうございます！ メッセージは送信されました。",
      "contact.error":
        "送信に失敗しました。お手数ですが再度お試しください。メールでもご連絡いただけます。",
      "contact.sending": "送信中…",
      "contact.required": "必須",
    },

    kr: {
      "intro.line1": "Creating a Better World Through the Power of Design.",
      "intro.line2":
        "그래픽 디자인, 브랜딩, 광고 크리에이티브 제작, SNS 마케팅, 영상 편집, 웹 제작.",
      "intro.line3":
        "브랜드와 서비스의 가치를 구현하여, 성과로 이어지는 크리에이티브를 제작합니다.",
      "services.brand.1": "브랜드 전략 / 네이밍 / 보이스 앤 톤 기획",
      "services.brand.2": "로고 & 아이덴티티 시스템 / 브랜드 가이드라인",
      "services.brand.3": "캠페인 크리에이티브 / SNS 콘텐츠 제작",
      "services.brand.4": "마케팅 자료 (포스터 / 전단지 / 옥외광고)",
      "services.print.1": "메뉴 / 브로슈어 / 카탈로그",
      "services.print.2": "명함 / 굿즈 / 패키징",
      "services.print.3": "사진 촬영 & 리터칭 (제품 / 음식 / 광고)",
      "services.print.4": "의류 디자인 (티셔츠 / 태그)",
      "services.print.5": "프레젠테이션 자료 / 자료 디자인",
      "services.print.6": "간판 디자인 (실내 / 실외 / 매장)",
      "services.web.1": "반응형 웹사이트 (HTML / CSS / JavaScript / PHP)",
      "services.web.2": "WordPress / 헤드리스 CMS / 성능 최적화",
      "services.web.3": "UI·UX 디자인 / 랜딩 페이지",
      "services.web.4": "영상 편집",
      "services.web.5": "모션 그래픽 / 모션디자인",

      "about.name": "미야자와 모에 (그래픽・웹 디자이너)",
      "about.desc.1":
        "2019년부터 그래픽 디자인을 중심으로 한국과 호주에서 브랜딩, 광고 크리에이티브 제작, SNS 마케팅, 영상 편집, 웹 제작까지 폭넓게 활동.",
      "about.desc.2":
        "마케팅 관점을 바탕으로 브랜드와 서비스의 스토리를 구현하며, 성과로 이어지는 크리에이티브를 추구.",
      "about.desc.3": "일본어・한국어・영어로 실무 커뮤니케이션 가능.",
      "about.strengths.1": "브랜딩 & 비주얼 아이덴티티",
      "about.strengths.2": "미니멀하고 세련된 디자인",
      "about.strengths.3": "타이포그래피 & 레이아웃 설계",
      "about.strengths.4": "브랜드 스토리텔링",
      "about.strengths.5": "글로벌 프로젝트 협업",
      "about.strengths.6": "세심한 디테일작업과 높은 완성도",
      "about.edu.1": "관광학 학사 - Tokai University (도쿄)",
      "about.edu.2":
        "마케팅·커뮤니케이션 (Certificate IV) - Greystone College (시드니)",
      "about.edu.3":
        "그래픽 디자인 (Diploma) - Australian Pacific College (시드니)",
      "about.edu.4": "웹 개발 (Certificate IV / Diploma) - TAFE NSW (시드니)",
      "about.langs.jp": "일본어 - 원어민",
      "about.langs.kr": "한국어 - 고급 (TOPIK 6급)",
      "about.langs.en": "영어 - 고급",

      "contact.firstName": "이름",
      "contact.lastName": "성",
      "contact.service": "의뢰 항목",
      "contact.service.select": "선택하세요",
      "contact.service.graphic": "그래픽 디자인",
      "contact.service.brand": "브랜딩",
      "contact.service.web": "웹사이트",
      "contact.service.video": "영상",
      "contact.service.apparel": "어패럴 / 굿즈",
      "contact.service.other": "기타",
      "contact.email": "이메일",
      "contact.message": "메시지",
      "contact.send": "보내기",
      "contact.err.firstName": "이름을 입력해 주세요.",
      "contact.err.email": "유효한 이메일 주소를 입력해 주세요.",
      "contact.err.message": "내용을 10자 이상 입력해 주세요.",
      "contact.success": "메시지가 전송되었습니다. 감사합니다!",
      "contact.error":
        "전송에 실패했습니다. 다시 시도해 주세요. 이메일로 연락 주셔도 됩니다.",
      "contact.sending": "전송 중…",
      "contact.required": "필수",
    },
  };

  /* -------------------------------------------------------
     Contact form validation（必要時だけ表示）
     ------------------------------------------------------- */
  function setFieldError(inputEl, on) {
    const wrap = inputEl.closest(".col-12, .col-md-6") || inputEl.parentElement;
    const msgEl = wrap ? wrap.querySelector(".error-msg") : null;
    if (!msgEl) return;

    if (on) {
      inputEl.setAttribute("aria-invalid", "true");
      msgEl.textContent = msgEl.dataset.msg || "";
      msgEl.classList.add("show");
    } else {
      inputEl.removeAttribute("aria-invalid");
      msgEl.textContent = "";
      msgEl.classList.remove("show");
    }
  }

  function validateField(el) {
    const val = (el.value || "").trim();

    if (el.type === "email" || el.name === "email") {
      const ok = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val);
      setFieldError(el, !ok);
      return ok;
    }

    if (el.name === "message") {
      const ok = val.length >= 10;
      setFieldError(el, !ok);
      return ok;
    }

    if (el.hasAttribute("required")) {
      const ok = val.length > 0;
      setFieldError(el, !ok);
      return ok;
    }

    setFieldError(el, false);
    return true;
  }

  function attachContactValidation() {
    const form = document.getElementById("contactForm");
    if (!form) return;

    const inputs = form.querySelectorAll("input, textarea, select");
    // blur / input の逐次チェック
    inputs.forEach((el) => {
      el.addEventListener("blur", () => validateField(el));
      el.addEventListener("input", () => {
        if (el.getAttribute("aria-invalid") === "true") validateField(el);
      });
    });

    // ★ここを置き換え
    form.addEventListener("submit", async (e) => {
      e.preventDefault();

      // まず全項目を検証して、必要なエラーは一気に表示
      let ok = true;
      const fields = form.querySelectorAll(
        'input[required], textarea[required], select[required], input[name="email"], textarea[name="message"]',
      );
      fields.forEach((el) => {
        if (!validateField(el)) ok = false;
      });
      if (!ok) {
        const firstInvalid = form.querySelector('[aria-invalid="true"]');
        firstInvalid?.focus();
        return;
      }

      // 送信中UI
      const orig = btn.textContent;
      btn.disabled = true;
      btn.textContent = dict["contact.sending"] || "Sending…";
      msgEl.textContent = "";
      msgEl.className = ""; // 状態クラスをクリア

      try {
        // 送信データ作成（選択言語も同梱）
        const fd = new FormData(form);
        fd.set("uiLang", lang);

        const res = await fetch(form.action, {
          method: "POST",
          body: fd,
          headers: { Accept: "application/json" },
        });

        if (res.ok) {
          form.reset();
          // エラー表示をクリア
          form
            .querySelectorAll('[aria-invalid="true"]')
            .forEach((el) => setFieldError(el, false));
          msgEl.textContent =
            dict["contact.sent"] || "Thanks! Your message has been sent.";
          msgEl.classList.add("ok");
        } else {
          msgEl.textContent =
            dict["contact.err.send"] ||
            "Sorry—couldn’t send. Please try again or email me directly.";
          msgEl.classList.add("err");
        }
      } catch {
        msgEl.textContent =
          dict["contact.err.send"] ||
          "Sorry—couldn’t send. Please try again or email me directly.";
        msgEl.classList.add("err");
      } finally {
        btn.disabled = false;
        btn.textContent = orig;
      }
    });
  }

  /* -------------------------------------------------------
  9) i18n: applyLang（error-msg は保持のみ）
------------------------------------------------------- */
  function applyLang(lang) {
    const dict = I18N[lang] || I18N.en;
    const htmlLang = lang === "jp" ? "ja" : lang === "kr" ? "ko" : "en";

    // 1) 通常テキストの差し替え（error-msg / i18n-live は除外）
    document.querySelectorAll("[data-i18n]").forEach((el) => {
      if (el.matches(".error-msg,[data-i18n-msg],[data-i18n-live]")) return;
      const key = el.getAttribute("data-i18n");
      if (!key || dict[key] == null) return;

      if (el.childElementCount === 0) {
        // 子要素が無ければ丸ごと置換
        el.textContent = dict[key];
      } else {
        // 子要素（アイコン等）を壊さず最初のテキストノードだけ置換
        const tn = Array.from(el.childNodes).find(
          (n) => n.nodeType === Node.TEXT_NODE,
        );
        if (tn) tn.nodeValue = dict[key];
        else el.append(document.createTextNode(dict[key]));
      }
    });

    // 2) エラーメッセージは data-msg に保持（表示中なら文言も即更新）
    document.querySelectorAll("[data-i18n-msg]").forEach((el) => {
      const key = el.getAttribute("data-i18n-msg");
      const val = key && dict[key] != null ? dict[key] : "";
      el.dataset.msg = val;
      if (el.classList.contains("show")) el.textContent = val;
    });

    // 3) フォント切替したいブロックだけ lang を付与
    document.querySelectorAll('[data-font-scope="1"]').forEach((el) => {
      if (htmlLang === "ja" || htmlLang === "ko")
        el.setAttribute("lang", htmlLang);
      else el.removeAttribute("lang");
    });

    // 4) （任意）UI言語を hidden に同期
    const uiLang = document.getElementById("uiLangField");
    if (uiLang) uiLang.value = lang;

    // 5) 送信後メッセージなど live 表示中テキストを現在言語で更新
    document.querySelectorAll("[data-i18n-live]").forEach((el) => {
      const key = el.getAttribute("data-i18n-live");
      if (!key) return;
      if (!el.textContent || !el.textContent.trim()) return; // 未表示なら無視
      if (dict[key] != null) el.textContent = dict[key];
    });

    // requiredバッジを現在言語に更新（CSS content 用）
    const req = dict["contact.required"] ?? I18N.en["contact.required"];
    const cssContent = (s) =>
      `"${String(s).replace(/\\/g, "\\\\").replace(/"/g, '\\"')}"`;
    document.documentElement.style.setProperty("--req-label", cssContent(req));

    // 6) 言語表示ボタンのラベル更新
    setButtonsLabel?.(lang);

    // 7) 名前欄の順序を言語によって切り替える（英語: First→Last / 日韓: Last→First）
    //    対象は #contactForm 内の最初の .row.g-3 とし、firstName/lastName の .col-md-6 を入れ替える
    (function reorderNameFields() {
      const form = document.getElementById("contactForm");
      if (!form) return;

      // “最初の”名前行を特定（あなたのマークアップに合わせて行います）
      const nameRow = form.querySelector(".row.g-3");
      if (!nameRow) return;

      const firstWrap = nameRow
        .querySelector('input[name="firstName"]')
        ?.closest(".col-md-6");
      const lastWrap = nameRow
        .querySelector('input[name="lastName"]')
        ?.closest(".col-md-6");
      if (!firstWrap || !lastWrap) return;

      // 目標順序：jp/kr → Last, First / それ以外 → First, Last
      const wantLastFirst = lang === "jp" || lang === "kr";

      // 既に希望順なら何もしない（無駄な reflow を避ける）
      const current = Array.from(nameRow.children).filter((n) =>
        n.classList?.contains("col-md-6"),
      );
      const isCurrentLastFirst =
        current[0] === lastWrap && current[1] === firstWrap;

      if (wantLastFirst && !isCurrentLastFirst) {
        nameRow.insertBefore(lastWrap, firstWrap);
      } else if (!wantLastFirst && isCurrentLastFirst) {
        nameRow.insertBefore(firstWrap, lastWrap);
      }

      // ついでにオートフィルも適切に動くよう autocomplete はそのまま維持（変更不要）
      // ラベル文言は data-i18n により上で更新済み
    })();
  }

  /* -------------------------------------------------------
     10) Lang switch UI（デスクトップ & モバイル同期）
     ------------------------------------------------------- */
  function setButtonsLabel(lang) {
    const btns = [
      document.getElementById("langDropdown"),
      document.getElementById("mobileLangDropdown"),
    ].filter(Boolean);
    btns.forEach((b) => {
      b.innerHTML = `<i class="fas fa-globe me-1"></i> ${lang.toUpperCase()}`;
    });
  }

  // クリック委譲（動的に追加されたDOMにも対応）
  document.addEventListener("click", (e) => {
    const opt = e.target.closest(".lang-option");
    if (!opt) return;
    e.preventDefault();
    const lang = opt.dataset.lang || "en";
    localStorage.setItem("lang", lang);
    applyLang(lang);
  });

  // 初期適用（一次）
  const saved = localStorage.getItem("lang") || "en";
  applyLang(saved);

  // 外部からも変更できるよう公開
  window.__setLang = (lang) => {
    localStorage.setItem("lang", lang);
    applyLang(lang);
  };

  /* -------------------------------------------------------
     11) Boot: inject partials then init features
     ------------------------------------------------------- */
  async function boot() {
    await Promise.allSettled([
      inject("#header", "partials/header.html"),
      inject("#drawer", "partials/mobile-drawer.html"),
      inject("#footer", "partials/footer.html"),
    ]);

    // 部分テンプレ挿入後にもう一度反映
    const cur = localStorage.getItem("lang") || "en";
    applyLang(cur);
    setButtonsLabel(cur);

    initMobileMenu();
    if (typeof initAboutFade === "function") initAboutFade();
    initBackToTop();
    initServicesAccordion();
    initGalleryModal();
    initLightbox();
    markActiveNav();

    attachContactValidation(); // 入力中チェック
    attachContactAjax(); // 送信処理
  }

  // 送信時だけ全体チェックするヘルパー
  function validateForm(form) {
    let ok = true;
    const fields = form.querySelectorAll(
      'input[required], textarea[required], select[required], input[name="email"], textarea[name="message"]',
    );
    fields.forEach((el) => {
      if (!validateField(el)) ok = false;
    });
    if (!ok) {
      const firstInvalid = form.querySelector('[aria-invalid="true"]');
      firstInvalid?.focus();
    }
    return ok;
  }

  function attachContactValidation() {
    const form = document.getElementById("contactForm");
    if (!form) return;

    const inputs = form.querySelectorAll("input, textarea, select");

    // 入力中の逐次バリデーションのみ（submit はここで扱わない）
    inputs.forEach((el) => {
      el.addEventListener("blur", () => validateField(el));
      el.addEventListener("input", () => {
        if (el.getAttribute("aria-invalid") === "true") validateField(el);
      });
    });
  }

  function attachContactAjax() {
    const form = document.getElementById("contactForm");
    if (!form) return;

    const btn = form.querySelector(".btn-submit");
    const msgEl = document.getElementById("formMessage");

    const getLang = () => localStorage.getItem("lang") || "en";
    const getDict = () => I18N[getLang()] || I18N.en;

    form.addEventListener("submit", async (e) => {
      e.preventDefault();

      // 1) まずバリデーション
      if (!validateForm(form)) return;

      // 2) 送信UI
      const dict = getDict();
      const origText = btn.textContent;
      btn.disabled = true;
      btn.textContent = dict["contact.sending"] || "Sending…";
      msgEl.textContent = "";
      msgEl.classList.remove("status-ok", "status-ng");

      // 3) 実送信（Formspree）
      try {
        const fd = new FormData(form);
        fd.set("uiLang", getLang());

        const res = await fetch(form.action, {
          method: "POST",
          body: fd,
          headers: { Accept: "application/json" },
        });

        if (res.ok) {
          // 成功
          form.reset();
          form.querySelectorAll('[aria-invalid="true"]').forEach((el) => {
            setFieldError(el, false);
          });

          // live テキスト：言語切替に追従させるためキーを保存
          msgEl.dataset.i18nLive = "contact.success";
          msgEl.textContent =
            dict["contact.success"] ||
            dict["contact.sent"] || // 互換
            "Thanks! Your message has been sent.";
          msgEl.classList.add("status-ok");
        } else {
          msgEl.dataset.i18nLive = "contact.error";
          msgEl.textContent =
            dict["contact.error"] ||
            "Sorry—couldn’t send. Please try again or email me directly.";
          msgEl.classList.add("status-ng");
        }
      } catch {
        msgEl.dataset.i18nLive = "contact.error";
        msgEl.textContent =
          dict["contact.error"] ||
          "Sorry—couldn’t send. Please try again or email me directly.";
        msgEl.classList.add("status-ng");
      } finally {
        btn.disabled = false;
        btn.textContent = origText;
      }
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot, { once: true });
  } else {
    boot();
  }
})();
